// apps/web/src/pages/settings/index.tsx — Phase 2 plan 02-04 B6 settings hub.
//
// Three sections per 02-UI-SPEC.md B6 layout:
//   - Reservierung: Hours (clickable), Slots/Capacity/Lead time/Same-day
//     cutoff/Min-Max (Bald verfügbar rows)
//   - Betrieb: Areas (clickable — toggle Area.enabled), Blocked periods /
//     Confirmation / Notifications / Retention / Users (Bald verfügbar)
//   - Bald verfügbar: Waitlist / Stats / Tables / Multi-tenant (placeholder)
//
// Clickable rows are <Link>s that navigate to the detail route.
// Non-clickable "Bald" rows render with a "Bald" tag and no chevron.
//
// Areas toggle: the row uses a switch primitive that calls
// toggleArea(key, enabled) and renders the new value immediately
// (TanStack optimistic update).

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { gqlRequest } from "../../lib/api";
import { AREAS_QUERY, TOGGLE_AREA_MUTATION } from "./operations";

interface TranslPair {
  de: string;
  en: string | null;
}

interface Area {
  id: string;
  key: "INNEN" | "TERRASSE";
  enabled: boolean;
  label: TranslPair;
}

interface AreasResponse {
  areas: Area[];
}

interface ToggleAreaResponse {
  toggleArea: { key: "INNEN" | "TERRASSE"; enabled: boolean };
}

function isEnglish(): boolean {
  try {
    return i18nLanguageStartsWithEn();
  } catch {
    return false;
  }
}

function i18nLanguageStartsWithEn(): boolean {
  // Lazy import to avoid SSR / circular-import surprises.
  // The function exists on the global `i18n` instance imported by the
  // caller via useTranslation; here we just need a hint for label
  // rendering, so a synchronous read of document.documentElement.lang
  // works too.
  if (typeof document !== "undefined") {
    return (document.documentElement.lang ?? "").toLowerCase().startsWith("en");
  }
  return false;
}

export function SettingsHubPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const areasQuery = useQuery<AreasResponse>({
    queryKey: ["settings", "areas"],
    queryFn: async () => gqlRequest<AreasResponse>(AREAS_QUERY),
  });

  const toggleMutation = useMutation({
    mutationFn: async (vars: { key: "INNEN" | "TERRASSE"; enabled: boolean }) =>
      gqlRequest<ToggleAreaResponse>(TOGGLE_AREA_MUTATION, vars),
    onMutate: async (vars) => {
      // Optimistic update so the toggle UI reflects the new value
      // immediately (Pitfall 9 — cache invalidation alone would wait
      // for the server round-trip).
      await queryClient.cancelQueries({ queryKey: ["settings", "areas"] });
      const prev = queryClient.getQueryData<AreasResponse>([
        "settings",
        "areas",
      ]);
      if (prev) {
        queryClient.setQueryData<AreasResponse>(["settings", "areas"], {
          areas: prev.areas.map((a) =>
            a.key === vars.key ? { ...a, enabled: vars.enabled } : a,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(["settings", "areas"], ctx.prev);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "areas"] });
    },
  });

  const areas = areasQuery.data?.areas ?? [];
  const innenEnabled = areas.find((a) => a.key === "INNEN")?.enabled ?? true;
  const terrasseEnabled =
    areas.find((a) => a.key === "TERRASSE")?.enabled ?? true;

  return (
    <main className="min-h-screen bg-bg text-text font-body">
      <header className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center justify-between">
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="settings-hub-title"
        >
          {t("settings.hub.title")}
        </h1>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="text-sm text-text/70 hover:text-text"
            data-testid="hub-logout-link"
          >
            {t("settings.logout")}
          </Link>
        </div>
      </header>

      <div className="px-4 py-6 w-full max-w-3xl space-y-8">
        <Section title={t("settings.hub.sectionReservierung")}>
          <Row
            to="/admin/einstellungen/oeffnungszeiten"
            label={t("settings.hub.rowHours")}
            testId="hub-row-hours"
            active
          />
          <Row
            to="/admin/einstellungen/verfuegbarkeit"
            label={t("settings.hub.rowAvailability")}
            testId="hub-row-availability"
            active
          />
          <Row
            to="/admin/einstellungen/restaurant"
            label={t("settings.hub.rowRestaurantProfile")}
            testId="hub-row-restaurant-profile"
            active
          />
        </Section>

        <Section title={t("settings.hub.sectionBetrieb")}>
          <AreasRow
            label={t("settings.hub.rowAreas")}
            innen={innenEnabled}
            terrasse={terrasseEnabled}
            loading={toggleMutation.isPending}
            onToggle={(key, enabled) => toggleMutation.mutate({ key, enabled })}
            testId="hub-row-areas"
          />
          {/* simplify-brevo-sender-config: the per-restaurant notification
              settings row is gone — sender identity is now derived from
              Restaurant.name + Restaurant.email, and the provider toggle
              lives in the JSONB column until a follow-up resurfaces a
              simpler switch UI. */}
          <BaldRow
            label={t("settings.hub.rowRetention")}
            testId="hub-row-retention"
          />
          <BaldRow label={t("settings.hub.rowUsers")} testId="hub-row-users" />
          {/* Quick-Task 260920-u3p: self-service admin email-change
              (Betrag block — Konto / Account). Routes to
              /admin/einstellungen/konto which fires
              requestAdminEmailChange + dispatches a magic link. */}
          <Row
            to="/admin/einstellungen/konto"
            label={t("settings.hub.rowAccount")}
            testId="hub-row-account"
            active
          />
        </Section>

        <Section title={t("settings.hub.sectionBald")}>
          <BaldRow
            label={t("settings.hub.rowWaitlist")}
            testId="hub-row-waitlist"
          />
          <BaldRow label={t("settings.hub.rowStats")} testId="hub-row-stats" />
          <BaldRow
            label={t("settings.hub.rowTables")}
            testId="hub-row-tables"
          />
          <BaldRow
            label={t("settings.hub.rowMultiTenant")}
            testId="hub-row-multi-tenant"
          />
        </Section>
      </div>
    </main>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-heading text-lg text-text/70 mb-2 uppercase tracking-wide">
        {title}
      </h2>
      <ul className="card divide-y divide-divider">{children}</ul>
    </section>
  );
}

function Row({
  to,
  label,
  testId,
  active,
}: {
  to: string;
  label: string;
  testId: string;
  active?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li>
      <Link
        to={to}
        className="flex items-center justify-between px-4 py-3 hover:bg-surface"
        data-testid={testId}
      >
        <span>{label}</span>
        <div className="flex items-center gap-2">
          {active && (
            <span className="tag" data-testid={`${testId}-tag`}>
              {t("settings.hub.tagActive")}
            </span>
          )}
          <ChevronRight size={20} aria-hidden="true" />
        </div>
      </Link>
    </li>
  );
}

function BaldRow({ label, testId }: { label: string; testId: string }) {
  const { t } = useTranslation();
  return (
    <li
      className="flex items-center justify-between px-4 py-3 text-text/60"
      data-testid={testId}
    >
      <span>{label}</span>
      <span className="tag" data-testid={`${testId}-tag`}>
        {t("settings.hub.tagBald")}
      </span>
    </li>
  );
}

function AreasRow({
  label,
  innen,
  terrasse,
  loading,
  onToggle,
  testId,
}: {
  label: string;
  innen: boolean;
  terrasse: boolean;
  loading: boolean;
  onToggle: (key: "INNEN" | "TERRASSE", enabled: boolean) => void;
  testId: string;
}) {
  const en = isEnglish();
  return (
    <li className="px-4 py-3" data-testid={testId}>
      <div className="font-medium mb-2">{label}</div>
      <div className="flex flex-wrap gap-4 items-center">
        <Switch
          label={en ? "Indoor" : "Innen"}
          enabled={innen}
          disabled={loading}
          onChange={(v) => onToggle("INNEN", v)}
          testId="hub-area-innen"
        />
        <Switch
          label={en ? "Terrace" : "Terrasse"}
          enabled={terrasse}
          disabled={loading}
          onChange={(v) => onToggle("TERRASSE", v)}
          testId="hub-area-terrasse"
        />
      </div>
    </li>
  );
}

function Switch({
  label,
  enabled,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  enabled: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className="inline-flex items-center gap-2 disabled:opacity-50"
      data-testid={testId}
    >
      <span
        className={`relative inline-block w-9 h-5 rounded-full transition-colors ${enabled ? "bg-accent" : "bg-divider"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${enabled ? "translate-x-4" : "translate-x-0"}`}
        />
      </span>
      <span className="text-sm">{label}</span>
    </button>
  );
}
